import {
  ears,
  eyes,
  faces,
  hairs,
  muzzles,
  patterns,
  brows,
  emptyShape
} from 'animal-avatar-generator/dist/shapes';
import { darken } from 'animal-avatar-generator/dist/utils/colors';
import { createSvg, createBackground } from 'animal-avatar-generator/dist/utils/svg';

export interface DirectAvatarConfig {
  // Colores principales
  primaryColor: string;
  secondaryColor: string;
  tertiaryColor: string;
  backgroundColor: string;

  // Colores específicos por parte
  faceColor: string;
  earColor: string;
  earInnerColor: string;
  eyeColor: string;
  muzzleColor: string;
  hairColor: string;
  browColor: string;
  patternColor: string;

  // Índices de variantes (0-based)
  earVariant: number;
  eyeVariant: number;
  faceVariant: number;
  hairVariant: number;
  muzzleVariant: number;
  browVariant: number;
  patternVariant: number;

  // Opciones
  hasPattern: boolean;
  hasHair: boolean;
  hasBrows: boolean;
  isRound: boolean;
  size: number;

  // Características avanzadas
  outlineWidth: number; // Grosor compartido
  useGradientBackground: boolean;
  gradientStartColor: string;
  gradientEndColor: string;
  avatarScale: number;

  // Configuración de contornos por parte
  faceOutlineEnabled: boolean;
  faceOutlineColor: string;
  earOutlineEnabled: boolean;
  earOutlineColor: string;
  eyeOutlineEnabled: boolean;
  eyeOutlineColor: string;
  muzzleOutlineEnabled: boolean;
  muzzleOutlineColor: string;
  hairOutlineEnabled: boolean;
  hairOutlineColor: string;
  browOutlineEnabled: boolean;
  browOutlineColor: string;
  patternOutlineEnabled: boolean;
  patternOutlineColor: string;

  // Control de colores específicos
  useIndividualColors: boolean;
}

export class DirectAnimalAvatar {

  static getDefaultConfig(): DirectAvatarConfig {
    return {
      primaryColor: '#F4A460',
      secondaryColor: '#8B4513',
      tertiaryColor: '#654321',
      backgroundColor: '#FFF8DC',
      earVariant: 0,
      eyeVariant: 0,
      faceVariant: 0,
      hairVariant: 0,
      muzzleVariant: 0,
      browVariant: 0,
      patternVariant: 0,
      hasPattern: false,
      hasHair: false,
      hasBrows: false,
      isRound: true,
      size: 150,

      // Defaults de contorno
      outlineWidth: 3,
      faceOutlineEnabled: false,
      faceOutlineColor: '#3d2b1f',
      earOutlineEnabled: false,
      earOutlineColor: '#3d2b1f',
      eyeOutlineEnabled: false,
      eyeOutlineColor: '#3d2b1f',
      muzzleOutlineEnabled: false,
      muzzleOutlineColor: '#3d2b1f',
      hairOutlineEnabled: false,
      hairOutlineColor: '#3d2b1f',
      browOutlineEnabled: false,
      browOutlineColor: '#3d2b1f',
      patternOutlineEnabled: false,
      patternOutlineColor: '#3d2b1f',

      useGradientBackground: false,
      gradientStartColor: '#FFF8DC',
      gradientEndColor: '#F0F8FF',
      avatarScale: 1.0,

      // Colores específicos
      faceColor: '#F4A460',
      earColor: '#F4A460',
      earInnerColor: '#FFDAB9',
      eyeColor: '#4682B4',
      muzzleColor: '#FFF5E1',
      hairColor: '#8B4513',
      browColor: '#8B4513',
      patternColor: '#FFF5E1',
      useIndividualColors: true
    };
  }

  static getAvailableVariants() {
    return {
      ears: ears?.length || 1,
      eyes: eyes?.length || 1,
      faces: faces?.length || 1,
      hairs: hairs?.length || 1,
      muzzles: muzzles?.length || 1,
      brows: brows?.length || 1,
      patterns: patterns?.length || 1
    };
  }

  static generateSVG(config: DirectAvatarConfig): string {
    const {
      primaryColor,
      earVariant,
      eyeVariant,
      faceVariant,
      hairVariant,
      muzzleVariant,
      browVariant,
      patternVariant,
      hasPattern,
      hasHair,
      hasBrows,
      isRound,
      size,
      outlineWidth,
      useGradientBackground,
      gradientStartColor,
      gradientEndColor,
      avatarScale
    } = config;

    // Determinar colores finales a usar
    const finalColors = this.getFinalColors(config);

    try {
      // Validar que los shapes están disponibles
      if (!ears || !Array.isArray(ears) || !faces || !Array.isArray(faces)) {
        return this.generateFallbackSVG(size, config.backgroundColor, primaryColor, config);
      }

      // Asegurar que los índices están en rango y son válidos
      const safeEarVariant = Math.max(0, Math.min(earVariant, ears.length - 1));
      const safeEyeVariant = Math.max(0, Math.min(eyeVariant, (eyes?.length || 1) - 1));
      const safeFaceVariant = Math.max(0, Math.min(faceVariant, faces.length - 1));
      const safeHairVariant = Math.max(0, Math.min(hairVariant, (hairs?.length || 1) - 1));
      const safeMuzzleVariant = Math.max(0, Math.min(muzzleVariant, (muzzles?.length || 1) - 1));
      const safeBrowVariant = Math.max(0, Math.min(browVariant, (brows?.length || 1) - 1));
      const safePatternVariant = Math.max(0, Math.min(patternVariant, (patterns?.length || 1) - 1));

      // Validar que las funciones existen antes de llamarlas
      const earFunction = ears[safeEarVariant];
      const faceFunction = faces[safeFaceVariant];

      if (typeof earFunction !== 'function' || typeof faceFunction !== 'function') {
        return this.generateFallbackSVG(size, config.backgroundColor, primaryColor, config);
      }

      // --- 1. GENERAR FILTROS SVG ---
      // Creamos definiciones de filtros para cada parte que tenga outline activado
      const defs: string[] = [];

      const faceFilterId = config.faceOutlineEnabled ? this.generateOutlineFilter(defs, config.faceOutlineColor, outlineWidth, 'face') : '';
      const earFilterId = config.earOutlineEnabled ? this.generateOutlineFilter(defs, config.earOutlineColor, outlineWidth, 'ear') : '';
      const eyeFilterId = config.eyeOutlineEnabled ? this.generateOutlineFilter(defs, config.eyeOutlineColor, outlineWidth, 'eye') : '';
      const muzzleFilterId = config.muzzleOutlineEnabled ? this.generateOutlineFilter(defs, config.muzzleOutlineColor, outlineWidth, 'muzzle') : '';
      const hairFilterId = config.hairOutlineEnabled ? this.generateOutlineFilter(defs, config.hairOutlineColor, outlineWidth, 'hair') : '';
      const browFilterId = config.browOutlineEnabled ? this.generateOutlineFilter(defs, config.browOutlineColor, outlineWidth, 'brow') : '';
      const patternFilterId = config.patternOutlineEnabled ? this.generateOutlineFilter(defs, config.patternOutlineColor, outlineWidth, 'pattern') : '';

      // Fondo con soporte para gradientes
      const gradientId = `bg_grad_${Math.random().toString(36).substr(2, 9)}`;
      if (useGradientBackground) {
        defs.push(`
          <linearGradient id="${gradientId}" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stop-color="${gradientStartColor}" />
            <stop offset="100%" stop-color="${gradientEndColor}" />
          </linearGradient>
        `);
      }

      // --- 2. GENERACION DE PARTES (RAW) ---

      // FACE
      const faceContent = faceFunction(finalColors.face);

      // EARS
      let earParts = earFunction(finalColors.ear);
      earParts = this.applyInnerEarColor(earParts, finalColors.earInner);

      // MUZZLE
      let muzzleContent = '';
      if (muzzles && muzzles[safeMuzzleVariant]) {
        const rawMuzzle = muzzles[safeMuzzleVariant](finalColors.muzzle);
        muzzleContent = this.fixSVGColors(rawMuzzle, finalColors.muzzle, 'muzzle');
      }

      // EYES
      let eyeContent = '';
      if (eyes && eyes[safeEyeVariant]) {
        const rawEye = eyes[safeEyeVariant](finalColors.eye);
        eyeContent = this.fixSVGColors(rawEye, finalColors.eye, 'eyes');
      }

      // HAIR
      let hairContent = '';
      if (hasHair && hairs && hairs[safeHairVariant]) {
        hairContent = hairs[safeHairVariant](finalColors.hair);
      }

      // PATTERN
      let patternContent = '';
      if (hasPattern && patterns && patterns[safePatternVariant]) {
        patternContent = patterns[safePatternVariant](finalColors.pattern);
      }

      // BROWS
      let browContent = '';
      if (hasBrows && brows && brows[safeBrowVariant]) {
        browContent = brows[safeBrowVariant](finalColors.brow);
      }

      // --- 3. APPLY FILTERS (WRAP IN GROUPS) ---
      // Envolvemos cada parte en un <g> con el filtro correspondiente si existe

      const faceGroup = faceFilterId ? `<g filter="url(#${faceFilterId})">${faceContent}</g>` : faceContent;
      const earGroup = earFilterId ? `<g filter="url(#${earFilterId})">${earParts}</g>` : earParts;
      const muzzleGroup = muzzleFilterId ? `<g filter="url(#${muzzleFilterId})">${muzzleContent}</g>` : muzzleContent;
      const eyeGroup = eyeFilterId ? `<g filter="url(#${eyeFilterId})">${eyeContent}</g>` : eyeContent;
      const hairGroup = hairFilterId ? `<g filter="url(#${hairFilterId})">${hairContent}</g>` : hairContent;
      const patternGroup = patternFilterId ? `<g filter="url(#${patternFilterId})">${patternContent}</g>` : patternContent;
      const browGroup = browFilterId ? `<g filter="url(#${browFilterId})">${browContent}</g>` : browContent;

      // --- 4. ASSEMBLE SVG ---

      const contentScale = Math.max(0.3, Math.min(2.5, avatarScale * 1.1));
      // Centrado geométrico exacto
      const translateOffset = 250 * (1 - contentScale);

      const backgroundRect = useGradientBackground
        ? `<rect width="100%" height="100%" ${isRound ? 'rx="250"' : ''} fill="url(#${gradientId})" />`
        : createBackground(isRound, config.backgroundColor);

      return `
        <svg
          xmlns="http://www.w3.org/2000/svg"
          version="1.1"
          width="${size}"
          height="${size}"
          viewBox="0 0 500 500"
          style="display: block;"
        >
          <defs>
            ${defs.join('\n')}
          </defs>
          ${backgroundRect}
          <g transform="translate(${translateOffset}, ${translateOffset}) scale(${contentScale})">
            ${faceGroup}
            ${patternGroup}
            ${earGroup}
            ${hairGroup}
            ${muzzleGroup}
            ${eyeGroup}
            ${browGroup}
          </g>
        </svg>
      `;

    } catch (error) {
      console.error('❌ Error generating avatar SVG:', error);
      return this.generateFallbackSVG(size, config.backgroundColor, primaryColor, config);
    }
  }

  // Helper para generar filtros de contorno
  private static generateOutlineFilter(defsCollector: string[], color: string, width: number, suffix: string): string {
    const id = `outline_${suffix}_${Math.random().toString(36).substr(2, 9)}`;
    // El filtro usa feMorphology para dilatar (expandir) la forma alpha, 
    // luego la colorea con feFlood + feComposite, 
    // y finalmente pone la forma original encima con feMerge.
    const filter = `
      <filter id="${id}" x="-50%" y="-50%" width="200%" height="200%">
        <feMorphology in="SourceAlpha" operator="dilate" radius="${width}" result="dilated"/>
        <feFlood flood-color="${color}" result="flood"/>
        <feComposite in="flood" in2="dilated" operator="in" result="coloredOutline"/>
        <feMerge>
          <feMergeNode in="coloredOutline"/>
          <feMergeNode in="SourceGraphic"/>
        </feMerge>
      </filter>
    `;
    defsCollector.push(filter);
    return id;
  }

  // SVG de fallback simple
  static generateFallbackSVG(size: number, backgroundColor: string, primaryColor: string, config: DirectAvatarConfig): string {
    const contentScale = Math.max(0.3, Math.min(2.5, config.avatarScale));
    const translateOffset = 250 * (1 - contentScale);
    const uniqueId = `grad_${Math.random().toString(36).substr(2, 9)}`;

    const bgFill = config.useGradientBackground
      ? `url(#${uniqueId})`
      : backgroundColor;

    return `
      <svg
        xmlns="http://www.w3.org/2000/svg"
        version="1.1"
        width="${size}"
        height="${size}"
        viewBox="0 0 500 500"
      >
        ${config.useGradientBackground ? `
          <defs>
            <linearGradient id="${uniqueId}" x1="0%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%" stop-color="${config.gradientStartColor}" />
              <stop offset="100%" stop-color="${config.gradientEndColor}" />
            </linearGradient>
          </defs>
        ` : ''}
        <rect width="500" height="500" rx="250" fill="${bgFill}" />
        <g transform="translate(${translateOffset}, ${translateOffset}) scale(${contentScale})">
          <circle cx="250" cy="200" r="120" fill="${primaryColor}" />
          <text x="250" y="400" text-anchor="middle" font-size="40">Error</text>
        </g>
      </svg>
    `;
  }

  // Seed mejorado para soportar nueva config
  static seedToConfig(seed: string): DirectAvatarConfig {
    let hash = 0;
    for (let i = 0; i < seed.length; i++) {
      const char = seed.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash;
    }

    const rng = this.createSeededRandom(Math.abs(hash));
    const variants = this.getAvailableVariants();
    const primary = this.getCuteColor(rng);
    const secondary = this.getCuteColor(rng);

    // Outline por defecto activado para dar efecto sticker, pero sutil
    const defaultOutlineColor = '#3d2b1f';
    const enableOutlines = rng() > 0.3; // 70% chance de outlines

    return {
      primaryColor: primary,
      secondaryColor: secondary,
      tertiaryColor: this.getCuteColor(rng),
      backgroundColor: this.getCutePastelColor(rng),
      earVariant: Math.floor(rng() * variants.ears),
      eyeVariant: Math.floor(rng() * variants.eyes),
      faceVariant: Math.floor(rng() * variants.faces),
      hairVariant: Math.floor(rng() * variants.hairs),
      muzzleVariant: Math.floor(rng() * variants.muzzles),
      browVariant: Math.floor(rng() * variants.brows),
      patternVariant: Math.floor(rng() * variants.patterns),
      hasPattern: rng() > 0.6,
      hasHair: rng() > 0.8,
      hasBrows: rng() > 0.5,
      isRound: true,
      size: 150,

      outlineWidth: 3,
      // Randomizar outlines por parte para variedad
      faceOutlineEnabled: enableOutlines,
      faceOutlineColor: defaultOutlineColor,
      earOutlineEnabled: enableOutlines,
      earOutlineColor: defaultOutlineColor,
      eyeOutlineEnabled: false, // Ojos sin outline por defecto se ven mejor
      eyeOutlineColor: defaultOutlineColor,
      muzzleOutlineEnabled: false, // Muzzle limpio por defecto
      muzzleOutlineColor: defaultOutlineColor,
      hairOutlineEnabled: enableOutlines,
      hairOutlineColor: defaultOutlineColor,
      browOutlineEnabled: false,
      browOutlineColor: defaultOutlineColor,
      patternOutlineEnabled: false,
      patternOutlineColor: defaultOutlineColor,

      useGradientBackground: rng() > 0.5,
      gradientStartColor: this.getCutePastelColor(rng),
      gradientEndColor: this.getCutePastelColor(rng),
      avatarScale: 1.0,
      faceColor: primary,
      earColor: primary,
      earInnerColor: this.getCuteAccentColor(rng),
      eyeColor: this.getBrightEyeColor(rng),
      muzzleColor: this.getCuteLightColor(rng),
      hairColor: secondary,
      browColor: secondary,
      patternColor: this.getCuteLightColor(rng),
      useIndividualColors: true
    };
  }

  static createSeededRandom(seed: number) {
    let current = seed;
    return () => {
      current = (current * 16807) % 2147483647;
      return (current - 1) / 2147483646;
    };
  }

  static getCuteColor(rng: () => number): string {
    const colors = [
      '#F4A460', '#DEB887', '#D2691E', '#CD853F', '#BC8F8F', '#8B4513',
      '#FFDEAD', '#FFA07A', '#FFB6C1', '#FFDAB9', '#E6E6FA', '#D8BFD8',
      '#B0C4DE', '#ADD8E6', '#C0C0C0',
    ];
    return colors[Math.floor(rng() * colors.length)];
  }

  static getCutePastelColor(rng: () => number): string {
    const colors = [
      '#FFF0F5', '#E6E6FA', '#F0FFF0', '#F0FFFF', '#FFF8DC', '#FAFAD2',
      '#FFE4E1', '#FFF5EE', '#F5F5F5', '#FFEBCD', '#E0FFFF', '#F0F8FF',
    ];
    return colors[Math.floor(rng() * colors.length)];
  }

  static getCuteLightColor(rng: () => number): string {
    const colors = ['#FFFFFF', '#FFFAF0', '#F8F8FF', '#FFFACD', '#FFE4B5'];
    return colors[Math.floor(rng() * colors.length)];
  }

  static getCuteAccentColor(rng: () => number): string {
    const colors = ['#FF69B4', '#FFB6C1', '#87CEEB', '#98FB98', '#DDA0DD', '#F08080'];
    return colors[Math.floor(rng() * colors.length)];
  }

  static getBrightEyeColor(rng: () => number): string {
    const colors = ['#4169E1', '#00BFFF', '#32CD32', '#FF69B4', '#8A2BE2', '#FF8C00', '#20B2AA', '#40E0D0'];
    return colors[Math.floor(rng() * colors.length)];
  }

  static normalizeConfig(config: Partial<DirectAvatarConfig>): DirectAvatarConfig {
    const defaults = this.getDefaultConfig();
    return { ...defaults, ...config };
  }

  static generateFromSeed(seed: string, customizations?: Partial<DirectAvatarConfig>): string {
    const baseConfig = this.seedToConfig(seed);
    const finalConfig = { ...baseConfig, ...customizations };
    return this.generateSVG(finalConfig);
  }

  private static fixSVGColors(svgString: string, expectedColor: string, partName: string): string {
    if (!svgString || !expectedColor) {
      return svgString;
    }

    let fixedSVG = svgString;

    if (partName === 'eyes') {
      fixedSVG = svgString.replace(/fill="([^"]+)"/g, (match, color) => {
        const colorLower = color.toLowerCase();
        if (colorLower === '#fff' || colorLower === '#ffffff' || colorLower === 'white') {
          return match;
        }
        if (['#000', '#000000', '#1a1a1a', '#2a2a2a', '#15212a'].includes(colorLower)) {
          return `fill="${expectedColor}"`;
        }
        return match;
      });
    } else if (partName === 'muzzle') {
      fixedSVG = svgString.replace(/fill="([^"]+)"/g, (match, color) => {
        const colorLower = color.toLowerCase();
        if (['#000', '#000000', '#000000ff'].includes(colorLower)) {
          return `fill="#3d2b1f"`;
        }
        return `fill="${expectedColor}"`;
      });
    } else {
      // Reemplazar fill y stroke para otros elementos
      fixedSVG = svgString
        .replace(/fill="[^"]*"/g, `fill="${expectedColor}"`)
        .replace(/stroke="[^"]*"/g, `stroke="${expectedColor}"`);
    }

    return fixedSVG;
  }

  // Se eliminó createScaledSvg ya que su lógica se movió a generateSVG para tener acceso a los defs

  private static getFinalColors(config: DirectAvatarConfig) {
    if (config.useIndividualColors) {
      return {
        face: config.faceColor,
        ear: config.earColor,
        earInner: config.earInnerColor,
        eye: config.eyeColor,
        muzzle: config.muzzleColor,
        hair: config.hairColor,
        brow: config.browColor,
        pattern: config.patternColor
      };
    } else {
      return {
        face: config.primaryColor,
        ear: config.primaryColor,
        earInner: config.tertiaryColor,
        eye: '#4169E1',
        muzzle: config.backgroundColor,
        hair: config.secondaryColor,
        brow: config.secondaryColor,
        pattern: config.secondaryColor
      };
    }
  }

  private static applyInnerEarColor(earSvg: string, innerEarColor: string): string {
    if (!earSvg || !innerEarColor) return earSvg;

    const hardCodedInnerColors = [
      '#fec3aa', '#ffb3a0', '#f4a690', '#e6a085',
      '#d49c7a', '#c8906f', '#bc8464', '#b07859', '#a46c4e', '#986043'
    ];

    let processedSvg = earSvg;
    hardCodedInnerColors.forEach(color => {
      const regex = new RegExp(color, 'gi');
      processedSvg = processedSvg.replace(regex, innerEarColor);
    });

    return processedSvg;
  }

  static generatePartPreviewSVG(part: 'face' | 'ear' | 'eye' | 'muzzle' | 'hair' | 'brow', variant: number, primaryColor: string, secondaryColor: string): string {
    try {
      const safeVariant = Math.max(0, variant); // Simple safety, detailed check inside

      let content = '';

      switch (part) {
        case 'face':
          if (faces && faces[safeVariant]) {
            content = faces[safeVariant](primaryColor);
          }
          break;
        case 'ear':
          if (ears && ears[safeVariant]) {
            content = ears[safeVariant](primaryColor);
            // Use secondary as inner color for preview
            content = this.applyInnerEarColor(content, secondaryColor);
          }
          break;
        case 'eye':
          if (eyes && eyes[safeVariant]) {
            content = eyes[safeVariant](primaryColor);
            content = this.fixSVGColors(content, primaryColor, 'eyes');
          }
          break;
        case 'muzzle':
          if (muzzles && muzzles[safeVariant]) {
            content = muzzles[safeVariant](primaryColor);
            content = this.fixSVGColors(content, primaryColor, 'muzzle');
          }
          break;
        case 'hair':
          if (hairs && hairs[safeVariant]) {
            content = hairs[safeVariant](primaryColor);
          }
          break;
        default:
          return '';
      }

      // Generate a small clean SVG for the icon
      // We use the standard 500x500 viewbox but scale it down in CSS usually
      return `
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 500 500" width="100%" height="100%">
          ${content}
        </svg>
      `;
    } catch (e) {
      console.warn('Error generating preview:', e);
      return '';
    }
  }
}
